import axios, { AxiosResponse } from "axios";
import {
  ClientConfigurationVersioningStatus,
  GraphLibraryType,
  GraphLibraryVersioningCheck,
  GraphLibraryVersioningStatus,
} from "../models/ClientConfiguration/VersioningState";
import useApi from "../services/ApiService";

/**
 * Server-supplied explanation for a failed request, falling back to the caller's wording.
 *
 * The API answers in Problem Details, and the versioning endpoints put the useful part there: which
 * root drive name resolved to nothing, which setting is missing. Discarding it leaves the operator a
 * generic "failed" for every distinct cause.
 */
const describeError = (error: unknown, fallback: string): Error => {
  if (axios.isAxiosError(error)) {
    const validationErrors: Record<string, string[]> | undefined = error.response?.data?.errors;
    if (validationErrors) {
      return new Error(Object.values(validationErrors).flat().join("\n"));
    }

    const detail: string | undefined =
      error.response?.data?.detail ?? error.response?.data?.title;
    if (detail) return new Error(detail);
  }

  return new Error(fallback);
};

export class GraphLibrariesRepository {
  private api = useApi();

  async getVersioningStatus(
    clientConfigurationId: string,
  ): Promise<ClientConfigurationVersioningStatus> {
    try {
      const response: AxiosResponse<ClientConfigurationVersioningStatus> = await this.api.get(
        `/api/GraphLibraries/versioning/${clientConfigurationId}`,
      );
      return response.data;
    } catch (error) {
      throw describeError(error, "Failed to fetch SharePoint versioning status");
    }
  }

  async getOrganizationVersioningStatus(): Promise<ClientConfigurationVersioningStatus[]> {
    try {
      const response: AxiosResponse<ClientConfigurationVersioningStatus[]> = await this.api.get(
        "/api/GraphLibraries/versioning",
      );
      return response.data;
    } catch (error) {
      throw describeError(
        error,
        "Failed to fetch SharePoint versioning status for the organization",
      );
    }
  }

  /**
   * Establishes the library's versioning now, where the app registration has a certificate to read
   * SharePoint with. Where it has none the stored state is returned unchanged and `wasReadDirectly`
   * is false — nothing is queued and no client is asked to re-check.
   */
  async checkVersioning(
    clientConfigurationId: string,
    libraryType: GraphLibraryType,
  ): Promise<GraphLibraryVersioningCheck> {
    try {
      const response: AxiosResponse<GraphLibraryVersioningCheck> = await this.api.post(
        `/api/GraphLibraries/versioning/${clientConfigurationId}/check`,
        null,
        { params: { libraryType } },
      );
      return response.data;
    } catch (error) {
      throw describeError(error, "Failed to check SharePoint versioning");
    }
  }

  /**
   * Switches versioning on or off in SharePoint for one library.
   *
   * Returns the state the server read back after the change, which is not necessarily the state that
   * was asked for: SharePoint validates a versioning payload as a whole and rolls the whole change back
   * on a rejected field, so the caller has to compare rather than assume. Throws when the change was
   * refused outright, carrying the server's own reason.
   */
  async setVersioning(
    clientConfigurationId: string,
    libraryType: GraphLibraryType,
    enableVersioning: boolean,
  ): Promise<GraphLibraryVersioningStatus> {
    try {
      const response: AxiosResponse<GraphLibraryVersioningStatus> = await this.api.post(
        `/api/GraphLibraries/versioning/${clientConfigurationId}/set`,
        { libraryType, enableVersioning },
      );
      return response.data;
    } catch (error) {
      throw describeError(error, "Failed to change SharePoint versioning");
    }
  }
}

export default GraphLibrariesRepository;
